/**
 * License Intelligence Service (BFF - Backend For Frontend)
 *
 * This service handles all API communication for the License Intelligence feature.
 * It acts as a bridge between React components and the backend API.
 *
 * Caching is handled by React Query or Redux, not by this service.
 * This service is responsible for:
 * - Making API calls
 * - Transforming backend responses to frontend format
 * - Error handling
 */

import type { License } from '../components/Dashboard/LicenseInventory/type';
import FINDINGS_MOCK_DATA from '../mock-data/findingsMockData';
import HEADER_MOCK_DATA from '../mock-data/headerMockData';
import KPI_CARDS_MOCK_DATA from '../mock-data/kpiCardsMockData';
import LICENSE_DETAILS_MOCK_DATA from '../mock-data/licenseDetailsMockData';
import LICENSE_INVENTORY_MOCK_DATA from '../mock-data/licenseInventoryMockData';
import type { DashboardMetrics, LicenseSnapshot } from '../types';

/**
 * Fetch header data (compliance score and last sync date)
 *
 * @returns Promise<{ complianceScore: number; lastSyncDate: string }> Header data
 * @throws Error if API call fails
 */
export const fetchHeaderData = (): Promise<{
  complianceScore: number;
  lastSyncDate: string;
}> => {
  try {
    // eslint-disable-next-line no-warning-comments
    // TODO: Replace with actual API call when backend is ready
    // const response = await fetch(`${BASE_URL}/header`);
    // if (!response.ok) {
    //   throw new Error(`HTTP ${response.status}: Failed to fetch header data`);
    // }
    // const data = await response.json();
    // return transformHeaderData(data);

    // For now, return mock data
    return Promise.resolve(HEADER_MOCK_DATA);
  } catch (error) {
    // Return mock data as fallback
    return Promise.resolve(HEADER_MOCK_DATA);
  }
};

/**
 * Fetch KPI cards data
 *
 * @returns Promise<unknown[]> KPI cards data
 * @throws Error if API call fails
 */
export const fetchKPICards = (): Promise<unknown[]> => {
  try {
    // eslint-disable-next-line no-warning-comments
    // TODO: Replace with actual API call when backend is ready
    // const response = await fetch(`${BASE_URL}/kpi-cards`);
    // if (!response.ok) {
    //   throw new Error(`HTTP ${response.status}: Failed to fetch KPI cards`);
    // }
    // const data = await response.json();
    // return transformKPICards(data);

    // For now, return mock data
    return Promise.resolve(KPI_CARDS_MOCK_DATA);
  } catch (error) {
    // Return mock data as fallback
    return Promise.resolve(KPI_CARDS_MOCK_DATA);
  }
};

/**
 * Fetch license inventory data
 *
 * @returns Promise<License[]> Array of licenses
 * @throws Error if API call fails
 */
export const fetchLicenseInventoryData = (): Promise<License[]> => {
  try {
    // eslint-disable-next-line no-warning-comments
    // TODO: Replace with actual API call when backend is ready
    // const params = new URLSearchParams();
    // if (filters) {
    //   Object.entries(filters).forEach(([key, value]) => {
    //     params.append(key, String(value));
    //   });
    // }
    // if (sortBy) {
    //   params.append('sortBy', sortBy);
    // }
    // if (sortOrder) {
    //   params.append('sortOrder', sortOrder);
    // }
    // const url = params.toString()
    //   ? `${BASE_URL}/inventory?${params.toString()}`
    //   : `${BASE_URL}/inventory`;
    // const response = await fetch(url);
    // if (!response.ok) {
    //   throw new Error(`HTTP ${response.status}: Failed to fetch license inventory`);
    // }
    // const data = await response.json();
    // return transformLicenseInventory(data);

    // For now, return mock data
    return Promise.resolve(LICENSE_INVENTORY_MOCK_DATA);
  } catch (error) {
    // Return mock data as fallback
    return Promise.resolve(LICENSE_INVENTORY_MOCK_DATA);
  }
};

/**
 * Fetch findings data
 *
 * @returns Promise<unknown[]> Findings data
 * @throws Error if API call fails
 */
export const fetchFindings = (): Promise<unknown[]> => {
  try {
    // eslint-disable-next-line no-warning-comments
    // TODO: Replace with actual API call when backend is ready
    // const response = await fetch(`${BASE_URL}/findings`);
    // if (!response.ok) {
    //   throw new Error(`HTTP ${response.status}: Failed to fetch findings`);
    // }
    // const data = await response.json();
    // return transformFindings(data);

    // For now, return mock data
    return Promise.resolve(FINDINGS_MOCK_DATA);
  } catch (error) {
    // Return mock data as fallback
    return Promise.resolve(FINDINGS_MOCK_DATA);
  }
};

/**
 * Fetch license details for a specific license
 *
 * @param licenseId - The ID of the license to fetch details for
 * @returns Promise<unknown> License details
 * @throws Error if API call fails
 */
export const fetchLicenseDetails = (licenseId: string): Promise<unknown> => {
  try {
    if (!licenseId) {
      throw new Error('License ID is required');
    }

    // eslint-disable-next-line no-warning-comments
    // TODO: Replace with actual API call when backend is ready
    // const response = await fetch(`${BASE_URL}/${licenseId}/details`);
    // if (!response.ok) {
    //   throw new Error(`HTTP ${response.status}: Failed to fetch license details`);
    // }
    // const data = await response.json();
    // return transformLicenseDetails(data);

    // For now, return mock data
    const mockDetail = LICENSE_DETAILS_MOCK_DATA[licenseId];

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!mockDetail) {
      throw new Error(`License details not found for ID: ${licenseId}`);
    }

    return Promise.resolve(mockDetail);
  } catch (error) {
    // Return mock data as fallback
    return Promise.resolve(LICENSE_DETAILS_MOCK_DATA[licenseId] ?? null);
  }
};

/**
 * Fetch dashboard KPI metrics
 *
 * @returns Promise<DashboardMetrics> Dashboard metrics data
 * @throws Error if API call fails
 */
export const fetchDashboardMetrics = (): Promise<DashboardMetrics> => {
  try {
    // eslint-disable-next-line no-warning-comments
    // TODO: Replace with actual API call when backend is ready
    // const response = await fetch(`${BASE_URL}/metrics`);
    // if (!response.ok) {
    //   throw new Error(`HTTP ${response.status}: Failed to fetch dashboard metrics`);
    // }
    // const data = await response.json();
    // return transformDashboardMetrics(data);

    // For now, return mock data transformed to DashboardMetrics format
    return Promise.resolve(transformDashboardMetrics({}));
  } catch (error) {
    // Return mock data as fallback
    return Promise.resolve(transformDashboardMetrics({}));
  }
};

/**
 * Fetch detailed snapshot for a specific license
 *
 * @param licenseId - The ID of the license to fetch details for
 * @returns Promise<LicenseSnapshot> Detailed license snapshot
 * @throws Error if API call fails
 */
export const fetchLicenseSnapshot = (
  licenseId: string
): Promise<LicenseSnapshot> => {
  try {
    if (!licenseId) {
      throw new Error('License ID is required');
    }

    // eslint-disable-next-line no-warning-comments
    // TODO: Replace with actual API call when backend is ready
    // const response = await fetch(`${BASE_URL}/${licenseId}/snapshot`);
    // if (!response.ok) {
    //   throw new Error(`HTTP ${response.status}: Failed to fetch license snapshot`);
    // }
    // const data = await response.json();
    // return transformLicenseSnapshot(data);

    // For now, return mock data transformed to LicenseSnapshot format
    return Promise.resolve(transformLicenseSnapshot({}));
  } catch (error) {
    // Return mock data as fallback
    return Promise.resolve(transformLicenseSnapshot({}));
  }
};

/**
 * Transform backend dashboard metrics response to frontend format
 *
 * @param data - Raw data from backend
 * @returns Transformed DashboardMetrics
 */
const transformDashboardMetrics = (
  data: Record<string, unknown>
): DashboardMetrics => {
  const licenseCostData = data.licenseCost as
    | Record<string, unknown>
    | undefined;
  const activeLicensesData = data.activeLicenses as
    | Record<string, unknown>
    | undefined;
  const utilizationData = data.utilization as
    | Record<string, unknown>
    | undefined;
  const costSavingsData = data.costSavingsPotential as
    | Record<string, unknown>
    | undefined;
  const riskAlertsData = data.riskAlerts as Record<string, unknown> | undefined;

  const getChangeType = (
    value: unknown,
    defaultValue: 'YoY' | 'MoM'
  ): 'YoY' | 'MoM' => {
    if (value === 'YoY' || value === 'MoM') {
      return value;
    }

    return defaultValue;
  };

  return {
    licenseCost: {
      value: (licenseCostData?.value as string) || '0',
      label: 'License Cost',
      change: (licenseCostData?.change as number) || 0,
      changeType: getChangeType(licenseCostData?.changeType, 'YoY'),
      trend: licenseCostData?.trend as 'up' | 'down' | undefined,
    },
    activeLicenses: {
      value: (activeLicensesData?.value as string) || '0',
      label: 'Active Licenses',
      change: (activeLicensesData?.change as number) || 0,
      changeType: getChangeType(activeLicensesData?.changeType, 'MoM'),
      trend: activeLicensesData?.trend as 'up' | 'down' | undefined,
    },
    utilization: {
      value: (utilizationData?.value as string) || '0%',
      label: 'License Utilization',
      change: (utilizationData?.change as number) || 0,
      changeType: getChangeType(utilizationData?.changeType, 'MoM'),
      trend: utilizationData?.trend as 'up' | 'down' | undefined,
    },
    costSavingsPotential: {
      value: (costSavingsData?.value as string) || '0',
      label: 'Cost Savings Potential',
      change: (costSavingsData?.change as number) || 0,
      changeType: getChangeType(costSavingsData?.changeType, 'YoY'),
    },
    riskAlerts: {
      value: (riskAlertsData?.value as string) || '0',
      label: 'Risk Alerts',
      change: (riskAlertsData?.change as number) || 0,
      changeType: getChangeType(riskAlertsData?.changeType, 'YoY'),
    },
  };
};

/**
 * Transform backend license snapshot response to frontend format
 *
 * @param data - Raw data from backend
 * @returns Transformed LicenseSnapshot
 */
const transformLicenseSnapshot = (
  data: Record<string, unknown>
): LicenseSnapshot => {
  const skusData = data.skus as Record<string, unknown>[] | undefined;
  const userRolesData = data.userRoles as Record<string, unknown>[] | undefined;
  const metricsData = data.metrics as Record<string, unknown> | undefined;
  const findingsData = data.findings as string[] | undefined;

  const skus = Array.isArray(skusData)
    ? skusData.map((sku) => ({
        name: (sku.name as string) || '',
        expiryDate: (sku.expiryDate as string) || '',
      }))
    : [];

  const userRoles = Array.isArray(userRolesData)
    ? userRolesData.map((role) => ({
        role: (role.role as string) || '',
        count: (role.count as number) || 0,
      }))
    : [];

  return {
    id: (data.id as string) || '',
    vendor: (data.vendor as string) || '',
    status: (data.status as string) || 'active',
    metrics: {
      totalSpend: (metricsData?.totalSpend as number) || 0,
      utilization: (metricsData?.utilization as number) || 0,
      costPerEmployee: (metricsData?.costPerEmployee as number) || 0,
      optimizationScore: (metricsData?.optimizationScore as number) || 0,
      riskAlerts: (metricsData?.riskAlerts as number) || 0,
    },
    skus,
    userRoles,
    findings: Array.isArray(findingsData) ? findingsData : [],
  };
};
